"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { Autocomplete, AutocompleteOption } from "@/components/molecules/Autocomplete/Autocomplete";
import { useGeolocation } from "@/components/modules/portfolio/api/queries/useGeoLocation";
import { GeolocationSchemaType } from "@/components/modules/portfolio/api/schemas/schemas";
import { debounce } from "@/lib/debounce";
import { cn } from "@/lib/utils";
type Props = {
  onSelect?: (location: GeolocationSchemaType) => void;
  width?: number;
  defaultValue?: string;
  displayName?: string;
};
export function SearchLocationByQueryInput(t0) {
  const $ = _c(36);
  const {
    onSelect,
    width,
    defaultValue,
    displayName
  } = t0;
  const t = useTranslations("modules.listings.addRoomListing.houseDetails");
  const [query, setQuery] = useState("");
  const [selectedValue, setSelectedValue] = useState(null);
  const {
    data,
    isLoading
  } = useGeolocation(query);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = debounce(value => {
      setQuery(value);
    }, 500);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleSearchChange = t1;
  let t2;
  if ($[1] !== defaultValue || $[2] !== displayName || $[3] !== selectedValue?.display_name || $[4] !== t) {
    t2 = displayName || selectedValue?.display_name || defaultValue || t("selectAddress");
    $[1] = defaultValue;
    $[2] = displayName;
    $[3] = selectedValue?.display_name;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const displayText = t2;
  let t3;
  if ($[6] !== data?.data) {
    t3 = data?.data ?? [];
    $[6] = data?.data;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = t3.map(_temp);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const options = t4;
  let t5;
  if ($[10] !== query || $[11] !== t) {
    t5 = query ? t("noAddressFound") : t("startTypingAddress");
    $[10] = query;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const emptyMessage = t5;
  let t6;
  if ($[13] !== width) {
    t6 = width ? {
      width
    } : undefined;
    $[13] = width;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("flex h-11 w-full max-w-full min-w-0 items-center justify-between gap-2 rounded-full border border-input bg-card px-4 text-sm md:text-base text-muted-foreground shadow-xs transition-[color,box-shadow] cursor-pointer focus-visible:outline-hidden focus-visible:ring-ring/20 focus-visible:ring-[3px]");
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== displayText) {
    t8 = <span className="flex-1 min-w-0 truncate text-left">{displayText}</span>;
    $[16] = displayText;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronsUpDown className="size-4 shrink-0 opacity-50" />;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t8) {
    t10 = <button type="button" role="combobox" style={t6} className={t7}>{t8}{t9}</button>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== onSelect) {
    t11 = (_value, option) => {
      const location = option.meta as GeolocationSchemaType;
      setSelectedValue(location);
      onSelect?.(location);
    };
    $[22] = onSelect;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t) {
    t12 = t("searchAddress");
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== selectedValue) {
    t13 = option_0 => {
      const isSelected = selectedValue && String(selectedValue.place_id) === option_0.value;
      return <div className="flex items-center gap-2 w-full"><span className="flex-1 truncate">{option_0.label}</span><Check className={cn("ml-auto size-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} /></div>;
    };
    $[26] = selectedValue;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== emptyMessage || $[29] !== isLoading || $[30] !== options || $[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = <Autocomplete trigger={t10} options={options} isLoading={isLoading} onSelect={t11} onSearchChange={handleSearchChange} emptyMessage={emptyMessage} searchPlaceholder={t12} renderOption={t13} data-sentry-element="Autocomplete" data-sentry-component="SearchLocationByQueryInput" data-sentry-source-file="SearchLocationByQueryInput.tsx" />;
    $[28] = emptyMessage;
    $[29] = isLoading;
    $[30] = options;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}
function _temp(loc) {
  return {
    value: String(loc.place_id),
    label: loc.display_name,
    meta: loc
  };
}